import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from './ScorecardNavigation';
import { config } from '../../config/environment';
import {
  getUpsellMatchedEvents,
  getUpsellDailyRevenue,
  getUpsellLocationTotals,
  getShiftTranscript,
} from '../../services/scopedReadsService';
import * as Sentry from '@sentry/react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

// ── Types ──────────────────────────────────────────────────────────────
interface MatchedEvent {
  start_time: number;
  interaction_type: string;
  item_name: string;
  price: string | number;
  quantity: number;
  match_rule: string;
  shift_created: string;
  server_name: string;
  location_id: string;
  location_name: string;
  location_timezone: string | null;
  transcript_text: string | null;
  shift_id: string;
  transcript_start_time: number | null;
}

interface TranscriptPart {
  shift_id: string;
  start_time: number;
  text: string;
}

interface DailyRevenue {
  event_date: string;
  daily_revenue: string | number;
  daily_matches: number;
}

interface LocationTotal {
  location_id: string;
  location_name: string;
  total_upsells: number;
}

// ── Helpers ────────────────────────────────────────────────────────────
const ptFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const ptDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function getTodayPT(): string {
  const parts = ptDateFormatter.formatToParts(new Date());
  const y = parts.find(p => p.type === 'year')!.value;
  const m = parts.find(p => p.type === 'month')!.value;
  const d = parts.find(p => p.type === 'day')!.value;
  return `${y}-${m}-${d}`;
}

function getMonthStartPT(dateStr: string): string {
  return dateStr.slice(0, 8) + '01';
}

function getWeekStartPT(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? 6 : day - 1; // Mon=0
  d.setDate(d.getDate() - diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// shift_created is already in local time — parse and add start_time seconds
function eventWallClock(shiftCreated: string, startTimeSec: number): string {
  const [datePart, timePart] = shiftCreated.replace('T', ' ').split(' ');
  const [h, m, s] = (timePart || '00:00:00').split(':').map(Number);
  const totalSeconds = h * 3600 + m * 60 + (s || 0) + startTimeSec;
  const newH = Math.floor(totalSeconds / 3600) % 24;
  const newM = Math.floor((totalSeconds % 3600) / 60);
  const ampm = newH >= 12 ? 'PM' : 'AM';
  const displayH = newH % 12 === 0 ? 12 : newH % 12;
  return `${displayH}:${String(newM).padStart(2, '0')} ${ampm}`;
}

function eventLocalHour(shiftCreated: string, startTimeSec: number): number {
  const timePart = shiftCreated.replace('T', ' ').split(' ')[1] || '00:00:00';
  const [h, m, s] = timePart.split(':').map(Number);
  const totalSeconds = h * 3600 + m * 60 + (s || 0) + startTimeSec;
  return Math.floor(totalSeconds / 3600) % 24;
}

function stationLabel(interactionType: string): string {
  // The tenant's prompt names the upsell moments (greeter_upsell,
  // cashier_upsell, entree_upsell, counter_upsell, etc.). Use the prefix
  // when we recognize one; otherwise the interaction_type itself, prettified.
  if (interactionType.startsWith('greeter_')) return 'Greeter';
  if (interactionType.startsWith('cashier_')) return 'Cashier';
  if (interactionType.startsWith('counter_')) return 'Counter';
  if (interactionType.startsWith('entree_')) return 'Entree';
  if (interactionType.startsWith('appetizer_')) return 'Appetizer';
  if (interactionType.startsWith('dessert_')) return 'Dessert';
  if (interactionType === 'drink_promotion') return 'Drink';
  return 'Server';
}

function matchRuleLabel(rule: string, labels?: Record<string, string>): string {
  // Labels are the tenant's metric_labels (e.g. {metric_1: 'Caviar Crêpe', ...}).
  // No SAJJ fallback — if no label found, render the rule key so it's at least
  // recognizable and we don't show wrong tenant-named columns.
  if (labels && labels[rule]) return labels[rule];
  if (rule === 'drink_promotion' && labels?.drink_promotion) return labels.drink_promotion;
  return rule;
}

function formatCurrency(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// ── Dummy data ─────────────────────────────────────────────────────────
const DUMMY_EVENTS: MatchedEvent[] = [
  { start_time: 120, interaction_type: 'greeter_upsell', item_name: 'Steak Shawarma Plate', price: '16.99', quantity: 1, match_rule: 'metric_1', shift_created: '2025-01-15T17:00:00', server_name: 'Ahmad K.', location_id: '1', location_name: 'Fremont', location_timezone: 'America/Los_Angeles', transcript_text: 'Have you tried our steak shawarma? It\'s our most popular item.', shift_id: 's1', transcript_start_time: 120 },
  { start_time: 340, interaction_type: 'cashier_upsell', item_name: 'Dubai Chocolate Bar', price: '8.99', quantity: 2, match_rule: 'metric_3', shift_created: '2025-01-15T17:00:00', server_name: 'Sarah M.', location_id: '1', location_name: 'Fremont', location_timezone: 'America/Los_Angeles', transcript_text: 'Would you like to add our Dubai chocolate for dessert?', shift_id: 's2', transcript_start_time: 340 },
  { start_time: 510, interaction_type: 'greeter_upsell', item_name: 'Doner Kebab Wrap', price: '14.49', quantity: 1, match_rule: 'metric_2', shift_created: '2025-01-15T18:30:00', server_name: 'Mike T.', location_id: '2', location_name: 'San Jose', location_timezone: 'America/Los_Angeles', transcript_text: 'Our doner kebab is made fresh, would you like to try it?', shift_id: 's3', transcript_start_time: 510 },
  { start_time: 200, interaction_type: 'greeter_upsell', item_name: 'Mango Lassi', price: '5.99', quantity: 1, match_rule: 'drink_promotion', shift_created: '2025-01-15T17:30:00', server_name: 'Ahmad K.', location_id: '1', location_name: 'Fremont', location_timezone: 'America/Los_Angeles', transcript_text: 'Can I get you a mango lassi to start?', shift_id: 's1', transcript_start_time: 200 },
  { start_time: 180, interaction_type: 'greeter_upsell', item_name: 'Steak Shawarma Plate', price: '16.99', quantity: 1, match_rule: 'metric_1', shift_created: '2025-01-15T19:00:00', server_name: 'Lisa R.', location_id: '2', location_name: 'San Jose', location_timezone: 'America/Los_Angeles', transcript_text: 'The steak shawarma is excellent, highly recommend it.', shift_id: 's4', transcript_start_time: 180 },
  { start_time: 600, interaction_type: 'cashier_upsell', item_name: 'Sparkling Water', price: '3.49', quantity: 2, match_rule: 'drink_promotion', shift_created: '2025-01-15T18:00:00', server_name: 'Sarah M.', location_id: '1', location_name: 'Fremont', location_timezone: 'America/Los_Angeles', transcript_text: 'Would you like to add a sparkling water?', shift_id: 's2', transcript_start_time: 600 },
];

const DUMMY_TRANSCRIPT_PARTS: TranscriptPart[] = [
  { shift_id: 's1', start_time: 115, text: 'Hi, welcome in! What can I get for you today?' },
  { shift_id: 's1', start_time: 118, text: 'Um, I\'m not sure yet, what do you recommend?' },
  { shift_id: 's1', start_time: 120, text: 'Have you tried our steak shawarma? It\'s our most popular item.' },
  { shift_id: 's1', start_time: 123, text: 'Oh that sounds good, I\'ll try that.' },
  { shift_id: 's1', start_time: 125, text: 'Great choice! Would you like a drink with that?' },
  { shift_id: 's1', start_time: 195, text: 'Anything else I can get for you?' },
  { shift_id: 's1', start_time: 198, text: 'What drinks do you have?' },
  { shift_id: 's1', start_time: 200, text: 'Can I get you a mango lassi to start?' },
  { shift_id: 's1', start_time: 203, text: 'Yeah that sounds refreshing, sure.' },
  { shift_id: 's1', start_time: 205, text: 'Perfect, coming right up!' },
  { shift_id: 's2', start_time: 335, text: 'Your total comes to twenty-four fifty.' },
  { shift_id: 's2', start_time: 338, text: 'Okay sounds good.' },
  { shift_id: 's2', start_time: 340, text: 'Would you like to add our Dubai chocolate for dessert?' },
  { shift_id: 's2', start_time: 343, text: 'Oh yes please, add two of those!' },
  { shift_id: 's2', start_time: 345, text: 'Great, I\'ll add that to your order.' },
  { shift_id: 's2', start_time: 595, text: 'Is there anything else?' },
  { shift_id: 's2', start_time: 598, text: 'Just some water please.' },
  { shift_id: 's2', start_time: 600, text: 'Would you like to add a sparkling water?' },
  { shift_id: 's2', start_time: 603, text: 'Sure, make it two.' },
  { shift_id: 's2', start_time: 605, text: 'You got it!' },
  { shift_id: 's3', start_time: 505, text: 'Welcome! First time here?' },
  { shift_id: 's3', start_time: 508, text: 'Yes it is, what\'s good here?' },
  { shift_id: 's3', start_time: 510, text: 'Our doner kebab is made fresh, would you like to try it?' },
  { shift_id: 's3', start_time: 513, text: 'Absolutely, I\'ll have one.' },
  { shift_id: 's3', start_time: 515, text: 'Excellent, you won\'t be disappointed.' },
  { shift_id: 's4', start_time: 175, text: 'Hey there, ready to order?' },
  { shift_id: 's4', start_time: 178, text: 'I think so, what do you suggest?' },
  { shift_id: 's4', start_time: 180, text: 'The steak shawarma is excellent, highly recommend it.' },
  { shift_id: 's4', start_time: 183, text: 'Sold! I\'ll take one.' },
  { shift_id: 's4', start_time: 185, text: 'Coming right up!' },
];

const DUMMY_DAILY: DailyRevenue[] = [
  { event_date: '2025-01-15', daily_revenue: '87.93', daily_matches: 6 },
  { event_date: '2025-01-14', daily_revenue: '65.47', daily_matches: 4 },
  { event_date: '2025-01-13', daily_revenue: '112.30', daily_matches: 8 },
  { event_date: '2025-01-12', daily_revenue: '45.98', daily_matches: 3 },
  { event_date: '2025-01-11', daily_revenue: '78.50', daily_matches: 5 },
  { event_date: '2025-01-10', daily_revenue: '92.15', daily_matches: 7 },
  { event_date: '2025-01-09', daily_revenue: '55.00', daily_matches: 4 },
  { event_date: '2025-01-08', daily_revenue: '103.20', daily_matches: 6 },
];

const DUMMY_TOTALS: LocationTotal[] = [
  { location_id: '1', location_name: 'Fremont', total_upsells: 12 },
  { location_id: '2', location_name: 'San Jose', total_upsells: 8 },
];

// ── Component ──────────────────────────────────────────────────────────
const UpsellDashboard: React.FC = () => {
  const { user } = useAuth();
  const { selectedTenant, metricLabels } = useTenant();

  const normalizedRole = useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return null;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);

  const [selectedDate, setSelectedDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const y = yesterday.getFullYear();
    const m = String(yesterday.getMonth() + 1).padStart(2, '0');
    const d = String(yesterday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [events, setEvents] = useState<MatchedEvent[]>([]);
  const [dailyRevenue, setDailyRevenue] = useState<DailyRevenue[]>([]);
  const [locationTotals, setLocationTotals] = useState<LocationTotal[]>([]);
  const [transcriptParts, setTranscriptParts] = useState<Record<string, TranscriptPart[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTranscriptEnter = useCallback((e: React.MouseEvent<HTMLElement>, idx: number) => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ top: rect.top, left: rect.left });
    setHoveredIdx(idx);
  }, []);

  const handleTranscriptLeave = useCallback(() => {
    tooltipTimeout.current = setTimeout(() => {
      setHoveredIdx(null);
      setTooltipPos(null);
    }, 300);
  }, []);

  const handleTooltipEnter = useCallback(() => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    tooltipTimeout.current = setTimeout(() => {
      setHoveredIdx(null);
      setTooltipPos(null);
    }, 150);
  }, []);

  // ── Fetch data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (config.useDummyData) {
      setEvents(DUMMY_EVENTS);
      setDailyRevenue(DUMMY_DAILY);
      setLocationTotals(DUMMY_TOTALS);
      // Group dummy transcript parts by shift_id
      const grouped: Record<string, TranscriptPart[]> = {};
      DUMMY_TRANSCRIPT_PARTS.forEach(tp => {
        if (!grouped[tp.shift_id]) grouped[tp.shift_id] = [];
        grouped[tp.shift_id].push(tp);
      });
      setTranscriptParts(grouped);
      return;
    }

    const tenantId = selectedTenant;
    if (!tenantId) {
      setEvents([]);
      setDailyRevenue([]);
      setLocationTotals([]);
      setTranscriptParts({});
      return;
    }

    const monthStart = getMonthStartPT(selectedDate);

    let cancelled = false;
    setIsLoading(true);

    // feat-133 Batch-5: tenant-scoped upsell-dashboard endpoints replace the
    // client-built Revel SQL (tenant bound server-side from UserContext; DP2
    // preserves revel_*). Same q1/q2/q3 row shapes → assembly unchanged. The
    // Promise.allSettled per-query tolerance is preserved.
    Promise.allSettled([
      getUpsellMatchedEvents(tenantId, { date: selectedDate }),
      getUpsellDailyRevenue(tenantId, { start: monthStart, end: selectedDate }),
      getUpsellLocationTotals(tenantId, { date: selectedDate }),
    ]).then(async ([r1, r2, r3]) => {
      if (cancelled) return;
      const matchedEvents: MatchedEvent[] = r1.status === 'fulfilled' ? r1.value : [];
      setEvents(matchedEvents);
      setDailyRevenue(r2.status === 'fulfilled' ? r2.value : []);
      setLocationTotals(r3.status === 'fulfilled' ? r3.value : []);

      // Fetch surrounding transcript parts for matched shifts. q4 reuses the
      // Batch-2 shift-transcript endpoint (authorized per shift → foreign shift
      // 404), one call per matched shift; a per-shift failure is skipped. Rows
      // are projected to q4's {shift_id, start_time, text} shape.
      const shiftIds = Array.from(new Set(matchedEvents.map(e => e.shift_id).filter(Boolean)));
      if (shiftIds.length > 0) {
        try {
          const grouped: Record<string, TranscriptPart[]> = {};
          await Promise.all(
            shiftIds.map(async (shiftId) => {
              try {
                const rows = await getShiftTranscript(shiftId);
                grouped[shiftId] = rows.map((tp) => ({
                  shift_id: shiftId,
                  start_time: tp.start_time as number,
                  text: tp.text,
                }));
              } catch {
                // Per-shift failure (e.g. 404) is non-fatal — skip that shift.
              }
            }),
          );
          if (!cancelled) setTranscriptParts(grouped);
        } catch (err) {
          console.warn('Failed to fetch transcript context:', err);
          Sentry.captureException(err);
        }
      } else {
        setTranscriptParts({});
      }

      setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedTenant, selectedDate]);

  // ── Derived data ───────────────────────────────────────────────────
  const todayRevenue = useMemo(() =>
    events.reduce((sum, e) => sum + parseFloat(String(e.price)) * (e.quantity || 1), 0),
    [events]
  );

  const chartData = useMemo(() => {
    if (events.length === 0) return [];
    const buckets: Record<number, number> = {};
    events.forEach(e => {
      const h = eventLocalHour(e.shift_created, e.start_time);
      buckets[h] = (buckets[h] || 0) + parseFloat(String(e.price)) * (e.quantity || 1);
    });
    const hours = Object.keys(buckets).map(Number).sort((a, b) => a - b);
    return hours.map(h => ({
      hour: `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`,
      revenue: Math.round(buckets[h] * 100) / 100,
    }));
  }, [events]);

  const locationScoreboard = useMemo(() => {
    const map: Record<string, { name: string; revenue: number; count: number }> = {};
    events.forEach(e => {
      if (!map[e.location_id]) map[e.location_id] = { name: e.location_name, revenue: 0, count: 0 };
      map[e.location_id].revenue += parseFloat(String(e.price)) * (e.quantity || 1);
      map[e.location_id].count += 1;
    });
    return Object.entries(map)
      .map(([id, v]) => ({ location_id: id, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [events]);

  const thisWeekTotal = useMemo(() => {
    const weekStart = getWeekStartPT(selectedDate);
    return dailyRevenue
      .filter(d => d.event_date >= weekStart)
      .reduce((s, d) => s + parseFloat(String(d.daily_revenue)), 0);
  }, [dailyRevenue, selectedDate]);

  const thisMonthTotal = useMemo(() =>
    dailyRevenue.reduce((s, d) => s + parseFloat(String(d.daily_revenue)), 0),
    [dailyRevenue]
  );

  const totalMatched = useMemo(() => events.length, [events]);

  const totalUpsells = useMemo(() =>
    locationTotals.reduce((s, l) => s + (parseInt(String(l.total_upsells), 10) || 0), 0),
    [locationTotals]
  );

  const matchRate = useMemo(() =>
    totalUpsells > 0 ? Math.round((totalMatched / totalUpsells) * 100) : 0,
    [totalMatched, totalUpsells]
  );

  const locationPerformance = useMemo(() => {
    const totalsMap: Record<string, LocationTotal> = {};
    locationTotals.forEach(l => { totalsMap[l.location_id] = l; });

    const matchedByLoc: Record<string, { count: number; revenue: number; name: string }> = {};
    events.forEach(e => {
      if (!matchedByLoc[e.location_id]) matchedByLoc[e.location_id] = { count: 0, revenue: 0, name: e.location_name };
      matchedByLoc[e.location_id].count += 1;
      matchedByLoc[e.location_id].revenue += parseFloat(String(e.price)) * (e.quantity || 1);
    });

    const allLocIds = new Set([...Object.keys(totalsMap), ...Object.keys(matchedByLoc)]);
    return Array.from(allLocIds).map(id => {
      const total = parseInt(String(totalsMap[id]?.total_upsells || 0), 10);
      const matched = matchedByLoc[id]?.count || 0;
      const name = matchedByLoc[id]?.name || totalsMap[id]?.location_name || id;
      return {
        location: name,
        matched,
        total,
        conversionRate: total > 0 ? Math.round((matched / total) * 100) : 0,
        revenue: matchedByLoc[id]?.revenue || 0,
      };
    }).sort((a, b) => b.conversionRate - a.conversionRate);
  }, [events, locationTotals]);

  // ── Transcript context (±5s around the linked transcript part) ────
  const transcriptContext = useMemo(() => {
    const contextMap: Record<number, TranscriptPart[]> = {};
    events.forEach((evt, idx) => {
      if (evt.transcript_start_time == null || !evt.shift_id) return;
      const parts = transcriptParts[evt.shift_id];
      if (!parts) return;
      const anchor = parseFloat(String(evt.transcript_start_time));
      contextMap[idx] = parts.filter(
        tp => parseFloat(String(tp.start_time)) >= anchor - 30 &&
              parseFloat(String(tp.start_time)) <= anchor + 30
      );
    });
    return contextMap;
  }, [events, transcriptParts]);

  // ── Admin without tenant ───────────────────────────────────────────
  const isAdminWithoutTenant = normalizedRole === 'Admin' && !selectedTenant;

  if (isAdminWithoutTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Tenant</h3>
              <p className="text-gray-600 mb-4">
                Please select a tenant from the dropdown in the navigation bar to view upsell dashboard data.
              </p>
              <div className="text-sm text-gray-500">
                Use the tenant selector in the top-left corner to choose which tenant's data to view.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header + date picker */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-display font-semibold text-ink">Upsell Dashboard</h1>
            <p className="text-lg text-gray-600">POS-matched upsell performance &amp; revenue attribution</p>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <>
            {/* ── Top row: Revenue + Chart | Location Scoreboard ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 items-start">
              {/* Left 2/3: Revenue card + chart */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Today's Attributed Revenue</p>
                    <p className="text-4xl font-display font-semibold text-ink">{formatCurrency(todayRevenue)}</p>
                  </div>
                  <span className="text-sm text-gray-500">{totalMatched} matched events</span>
                </div>

                {/* Area chart */}
                <div className="h-56">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={(v: number | undefined) => [formatCurrency(v ?? 0), 'Revenue']} />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke="#10b981"
                          fill="url(#colorRevenue)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                      No revenue data for this date
                    </div>
                  )}
                </div>

                {/* Summary cards row */}
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">This Month</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(thisMonthTotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Match Rate</p>
                    <p className="text-lg font-semibold text-gray-900">{matchRate}%</p>
                    <p className="text-xs text-gray-400">{totalMatched}/{totalUpsells} interactions</p>
                  </div>
                </div>
              </div>

              {/* Right 1/3: Location scoreboard (~8 stores visible, scrolls for more) */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Location Scoreboard</h3>
                <div className="overflow-y-auto max-h-[350px]">
                  {locationScoreboard.length > 0 ? (
                    <div className="space-y-3">
                      {locationScoreboard.map((loc, i) => (
                        <div key={loc.location_id} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              i === 0 ? 'bg-yellow-100 text-yellow-700' :
                              i === 1 ? 'bg-gray-100 text-gray-600' :
                              'bg-orange-50 text-orange-600'
                            }`}>
                              {i + 1}
                            </span>
                            <span className="text-sm font-medium text-gray-900">{loc.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{formatCurrency(loc.revenue)}</p>
                            <p className="text-xs text-gray-400">{loc.count} items</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No location data</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Matched Upsell Events Table ── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden flex flex-col" style={{ maxHeight: '33vh' }}>
              <div className="px-6 py-4 border-b border-gray-100 shrink-0">
                <h3 className="text-lg font-medium text-gray-900">Matched Upsell Events</h3>
                <p className="text-sm text-gray-500">POS items attributed to AI-detected upsell interactions</p>
              </div>
              <div className="overflow-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transcript</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POS Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Station</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {events.length > 0 ? events.map((evt, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                          {eventWallClock(evt.shift_created, evt.start_time)}
                        </td>
                        <td
                          className="px-6 py-3 text-sm text-gray-600 max-w-xs truncate cursor-default"
                          onMouseEnter={e => handleTranscriptEnter(e, idx)}
                          onMouseLeave={handleTranscriptLeave}
                        >
                          {evt.transcript_text || <span className="text-gray-300 italic">no transcript</span>}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                          <div>{evt.item_name}</div>
                          <div className="text-xs text-gray-400">{matchRuleLabel(evt.match_rule, metricLabels)}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            stationLabel(evt.interaction_type) === 'Greeter'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {stationLabel(evt.interaction_type)}
                          </span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                          {formatCurrency(parseFloat(String(evt.price)) * (evt.quantity || 1))}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-400">
                          No matched upsell events for this date
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Location Revenue Bar Chart ── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-1">Revenue by Location</h3>
              <p className="text-sm text-gray-500 mb-4">Attributed upsell revenue per location</p>
              {locationPerformance.length > 0 ? (
                <>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={locationPerformance} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="location" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={(v: number | undefined) => [formatCurrency(v ?? 0), 'Revenue']} />
                        <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Stats row under bar chart */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                    {locationPerformance.map(lp => (
                      <div key={lp.location} className="text-center">
                        <p className="text-sm font-medium text-gray-900">{lp.location}</p>
                        <p className="text-xs text-gray-500">{formatCurrency(lp.revenue)}</p>
                        <p className="text-xs text-gray-500">{lp.matched} matched items</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                  No location revenue data for this date
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Fixed-position transcript context tooltip */}
      {hoveredIdx != null && tooltipPos && transcriptContext[hoveredIdx] && transcriptContext[hoveredIdx].length > 0 && (
        <div
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          className="fixed z-[9999] w-96 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3"
          style={{
            top: tooltipPos.top - 8,
            left: tooltipPos.left,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {transcriptContext[hoveredIdx].map((tp, tpIdx) => {
              const evt = events[hoveredIdx];
              const isAnchor = evt.transcript_start_time != null &&
                parseFloat(String(tp.start_time)) === parseFloat(String(evt.transcript_start_time));
              return (
                <p key={tpIdx} className={isAnchor ? 'font-semibold text-emerald-300' : 'text-gray-300'}>
                  {tp.text}
                </p>
              );
            })}
          </div>
          <div className="absolute left-6 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

export default UpsellDashboard;
